import { useEffect, useState } from "react";
import { costApi, type CostReference } from "../lib/api";
import { fmtAmount, parseAmount } from "../lib/money";
import { costProblem, type CostShare } from "./entry";

// 262_a: a line shared over cost centres. Shown only for accounts tracked by cost centre (Accounting › Accounts); the
// shares go by amount or percentage and must cover the line exactly (the server checks it again).
const cache = new Map<string, Promise<CostReference>>();
export function useCostRef(company: string): CostReference | null {
  const [r, setR] = useState<CostReference | null>(null);
  useEffect(() => {
    let live = true;
    if (!cache.has(company)) cache.set(company, costApi.reference(company).catch(() => ({ centres: [], tracked: [] })));
    void cache.get(company)!.then((x) => { if (live) setR(x); });
    return () => { live = false; };
  }, [company]);
  return r;
}
export const forgetCostRef = (company: string) => cache.delete(company);

export function CostSplit({ company, ledgerId, amount, cost, pct, onChange }: {
  company: string; ledgerId: string | null; amount: number | null; cost?: CostShare[]; pct?: boolean;
  onChange: (p: { cost: CostShare[]; costPct: boolean }) => void;
}) {
  const ref = useCostRef(company);
  const shares = cost ?? [];
  const [open, setOpen] = useState(false);
  if (!ref || !ledgerId || !ref.tracked.includes(ledgerId)) return null;
  const problem = costProblem({ cost: shares, costPct: pct }, amount);
  const set = (i: number, p: Partial<CostShare>) => onChange({ cost: shares.map((s, j) => (j === i ? { ...s, ...p } : s)), costPct: !!pct });
  const used = shares.filter((s) => s.cost_centre_id).length;
  if (!open) {
    return <div className="cost-split-link small">
      <button type="button" className={`link small${problem ? " danger-link" : ""}`} onClick={() => { setOpen(true); if (!shares.length) onChange({ cost: [{ cost_centre_id: null, value: "100" }], costPct: true }); }}>
        {used ? `Cost centres (${used})` : "Cost centres"}</button>
      {problem && <span className="err-text"> {problem}</span>}
    </div>;
  }
  const total = shares.reduce((t, s) => t + (parseAmount(s.value) ?? 0), 0);
  return (
    <div className={`cost-split${problem ? " bad" : ""}`}>
      {ref.centres.length === 0 && <p className="muted small">No cost centres yet: add them under Accounting › Cost centres.</p>}
      {shares.map((s, i) => (
        <div key={i} className="cost-share">
          <select value={s.cost_centre_id ?? ""} aria-label="Cost centre" className={!s.cost_centre_id && problem ? "invalid" : undefined}
            onChange={(e) => set(i, { cost_centre_id: e.target.value || null })}>
            <option value="">Choose a cost centre</option>
            {ref.centres.map((c) => <option key={c.id} value={c.id}>{c.name}{c.category !== "Cost categories" ? ` · ${c.category}` : ""}</option>)}
          </select>
          <input className={`num short${problem && !(parseAmount(s.value) ?? 0) ? " invalid" : ""}`} inputMode="decimal" aria-label={pct ? "Share %" : "Amount"} value={s.value}
            onChange={(e) => set(i, { value: e.target.value })} />
          <span className="muted small">{pct ? "%" : ""}</span>
          <button type="button" className="link" aria-label="Remove share" onClick={() => onChange({ cost: shares.filter((_, j) => j !== i), costPct: !!pct })}>✕</button>
        </div>))}
      <div className="row small">
        <button type="button" className="link small" onClick={() => onChange({ cost: [...shares, { cost_centre_id: null, value: pct ? String(Math.max(0, 100 - total)) : amount != null ? String(Math.max(0, Math.round((amount - total) * 100) / 100)) : "" }], costPct: !!pct })}>+ Add a share</button>
        <button type="button" className="link small" onClick={() => onChange({ cost: shares.map((s) => ({ ...s, value: "" })), costPct: !pct })}>{pct ? "Use amounts" : "Use percentages"}</button>
        <span className="muted small">{pct ? `${Math.round(total * 100) / 100}% of 100%` : `${fmtAmount(total)} of ${fmtAmount(amount ?? 0)}`}</span>
        <button type="button" className="link small" onClick={() => setOpen(false)}>Done</button>
      </div>
      {problem && <p className="err-text small" role="alert">{problem[0].toUpperCase() + problem.slice(1)}.</p>}
    </div>
  );
}
